/**
 * Tracks whether the user is currently navigating by keyboard or by pointer.
 *
 * Needed because CSS alone cannot answer the question for every control: `:focus-visible`
 * matches a text input even on a mouse click, and `:focus-within` matches any focus at all.
 * So anything that wants a keyboard-only affordance has to ask.
 *
 * State is module-level, not per-component, for two reasons: the modality is a property of
 * the user rather than of any one component, and a component that remounts mid-interaction
 * (switching date modes swaps one picker for two) would otherwise start out believing the
 * user is on a mouse and miss the keypress that caused the remount.
 */

/** Keys that mean the user is navigating by keyboard rather than pointer. */
export const NAV_KEYS = [
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Enter',
  'Escape',
  ' ',
];

let usingKeyboard = false;
let isListening = false;
const subscribers = new Set();

const notify = () => {
  subscribers.forEach((onChange) => onChange(usingKeyboard));
};

const handleKeyDown = (e) => {
  if (NAV_KEYS.includes(e.key) && !usingKeyboard) {
    usingKeyboard = true;
    notify();
  }
};

const handlePointerDown = () => {
  if (usingKeyboard) {
    usingKeyboard = false;
    notify();
  }
};

/** Whether the last interaction was a keypress rather than a pointer press. */
export const isKeyboardNav = () => usingKeyboard;

/**
 * Starts tracking, and optionally calls `onChange(usingKeyboard)` whenever the mode flips.
 * Returns an unsubscribe function. Callers that only need to read the current mode can skip
 * `onChange` and use `isKeyboardNav()`.
 *
 * Listeners are capture-phase so nothing downstream can hide the interaction from us, and
 * they are attached once and left in place -- two document listeners for the app's lifetime,
 * which avoids a teardown race when one subscriber unmounts as another mounts.
 */
export const subscribeKeyboardNav = (onChange) => {
  if (onChange) {
    subscribers.add(onChange);
    /* Sync immediately: the mode is global and may already be 'keyboard' before this
       subscriber mounted, and notifications only fire when it flips. Without this, a modal
       opened mid-keyboard-navigation would show no focus rings until the next mouse click. */
    onChange(usingKeyboard);
  }
  if (!isListening) {
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    isListening = true;
  }
  return () => {
    if (onChange) {
      subscribers.delete(onChange);
    }
  };
};
